import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * Posture chip — load-bearing E10 primitive.
 *
 * Mirrors `prototype/ui.js → postureChip()` and the `.chip-{healthy|at-risk|
 * critical|unassessed}` rules in `prototype/tokens.css`. Used on register
 * rows, scorecard cells, AI proposal queue items, finding cards, domain
 * assessment cards — anywhere a status posture is rendered (~50 places).
 *
 * Visual contract (token-driven, no raw hex):
 *   - 22px tall pill, rounded full, 0 10px horizontal padding
 *   - tinted background + tinted-darker ink (posture tokens)
 *   - 12px / 500 weight UI font
 *   - optional 6px dot prefix in `currentColor`
 *
 * Accessibility: carries `aria-label="Posture: {status}"` so screen readers
 * announce the semantic status independent of the visible label text.
 *
 * Default label per T-0092 ticket spec is the status capitalized
 * ("Healthy", "At risk", "Critical", "Unassessed"). The prototype's
 * `postureLabel` map uses "On track" / "Not rated" for two of these; the
 * TA contract wins per CLAUDE.md §3 ("Where the prototype and the TA
 * contract disagree, the TA contract wins."). Pass `children` to override.
 *
 * Required reading: `design_handoff_caliber_v2/README.md` §6 (Posture chip).
 */
export type PostureStatus = 'healthy' | 'at-risk' | 'critical' | 'unassessed';

export interface PostureChipProps {
  status: PostureStatus;
  /** Prefix the chip with a 6px dot indicator in the posture ink color. */
  dot?: boolean;
  /** Override the default capitalized status label. */
  children?: React.ReactNode;
  /** Optional class merge — kept minimal; per design tokens-only is preferred. */
  className?: string;
  /** What a screen reader announces; defaults to "Posture: {status}". */
  'aria-label'?: string;
}

const STATUS_LABEL: Record<PostureStatus, string> = {
  healthy: 'Healthy',
  'at-risk': 'At risk',
  critical: 'Critical',
  unassessed: 'Unassessed',
};

const STATUS_CLASSES: Record<PostureStatus, string> = {
  // posture-{role}-tint background paired with posture-{role} text (the
  // "tinted bg + tinted-darker ink" rule from README §6 / tokens.css).
  healthy: 'bg-posture-healthy-tint text-posture-healthy',
  'at-risk': 'bg-posture-at-risk-tint text-posture-at-risk',
  critical: 'bg-posture-critical-tint text-posture-critical',
  // Unassessed has no tint pair — falls back to ink-100 / posture-unassessed
  // (matches `.chip-unassessed` in prototype tokens.css).
  unassessed: 'bg-ink-100 text-posture-unassessed',
};

export function PostureChip({
  status,
  dot,
  children,
  className,
  'aria-label': ariaLabel,
}: PostureChipProps) {
  const label = children ?? STATUS_LABEL[status];
  return (
    <span
      role="status"
      aria-label={ariaLabel ?? `Posture: ${status}`}
      className={cn(
        // Base `.chip` rule from prototype tokens.css: inline-flex pill,
        // 22px tall, 0 10px padding, 6px gap, 12px / 500 / 18px line-height,
        // whitespace-nowrap.
        'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5',
        'text-meta font-medium leading-[18px]',
        STATUS_CLASSES[status],
        className,
      )}
    >
      {dot ? (
        <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
      ) : null}
      {label}
    </span>
  );
}
